import type { SearchResultItem } from "@/app/services/generated/searchService";
import {
  SearchQuery,
  SearchService,
} from "@/app/services/generated/searchService";
import type Guid from "@/app/types/common/guid";
import messages, { translate, keys } from "@/plugins/i18n";
import type { RawLocation } from "vue-router";
import { routes } from "@/app/ui/navigation/menuItems";
import { contactsMenu } from "@/app/ui/navigation/contactsMenu";
import { EnumBmEntityType } from "../generated/enums/enumBmEntityType.generated";
import { ullageRoutes } from "@/app/ui/navigation/ullagesMenu";
import { priceListSubRoutes } from "@/app/ui/navigation/pricingMenu";
import { signInWithQuery } from "@/app/ui/navigation/accountManagement";
import { salesMenu } from "@/app/ui/navigation/salesMenu";
import { adminRoutes } from "@/app/ui/navigation/administrationMenu";

export class SearchResultItemType {
  private static allValues: SearchResultItemType[] = []; // make sure this is top-most

  public static outlet = new SearchResultItemType(
    "Outlet",
    EnumBmEntityType.outlet.translation,
    (result) => contactsMenu.viewOutlet(result.objectId)
  );
  public static outletContact = new SearchResultItemType(
    "OutletContact",
    messages.search_type_outlet_contact,
    (result) => {
      return {
        name: routes.contactsMenu.outlets.contacts,
        params: { id: result.objectId.toString() },
      };
    }
  );
  public static group = new SearchResultItemType(
    "Group",
    EnumBmEntityType.outletGroup.translation,
    (result) => {
      return {
        name: routes.contactsMenu.outletGroups.view,
        params: { id: result.objectId.toString() },
      };
    }
  );
  public static groupContact = new SearchResultItemType(
    "GroupContact",
    messages.search_type_group_contact,
    (result) => {
      return {
        name: routes.contactsMenu.outletGroups.contacts,
        params: { id: result.objectId.toString() },
      };
    }
  );
  public static order = new SearchResultItemType(
    "Order",
    EnumBmEntityType.order.translation,
    (result) => routes.salesMenu.viewOrder(result.objectId)
  );
  public static creditNote = new SearchResultItemType(
    "CreditNote",
    EnumBmEntityType.credit.translation,
    (result) => salesMenu.viewCreditNote(result.objectId)
  );
  public static material = new SearchResultItemType(
    "Material",
    EnumBmEntityType.material.translation,
    (result) => {
      return {
        name: routes.stockMenu.stock.view,
        params: { id: result.objectId.toString() },
      };
    }
  );
  public static product = new SearchResultItemType(
    "Product",
    EnumBmEntityType.product.translation,
    (result) => {
      return {
        name: routes.stockMenu.stock.view,
        params: { id: result.objectId.toString() },
      };
    }
  );
  public static autoAssembledItem = new SearchResultItemType(
    "AutoAssembledItem",
    messages.search_type_auto_assembled_item,
    (result) => {
      return {
        name: routes.stockMenu.stock.view,
        params: { id: result.objectId.toString() },
      };
    }
  );

  public static deliveryService = new SearchResultItemType(
    "DeliveryService",
    messages.distribution_delivery_service_noun,
    (result) => {
      return {
        name: routes.distributionMenu.settings.deliveryServices.edit,
        params: { id: result.objectId.toString(), mode: "view" },
      };
    }
  );

  public static bottleDepositCharge = new SearchResultItemType(
    "BottleDepositCharge",
    messages.bottle_deposit_charge_item_noun,
    (result) =>
      adminRoutes.editBottleDepositChargeItem.location({
        id: result.objectId,
        editing: false,
      })
  );

  public static bottleDepositRedemption = new SearchResultItemType(
    "BottleDepositRedemption",
    messages.bottle_deposit_redemption_item_noun,
    (result) =>
      adminRoutes.editBottleDepositRedemptionItem.location({
        id: result.objectId,
        editing: false,
      })
  );

  public static recipe = new SearchResultItemType(
    "MaterialProduction",
    messages.search_type_recipe,
    (result) => {
      return {
        name: routes.productionMenu.recipe.view,
        params: { id: result.objectId.toString() },
      };
    }
  );
  public static packaging = new SearchResultItemType(
    "ProductPackaging",
    messages.search_type_packaging,
    (result) => {
      return {
        name: routes.productionMenu.packaging.view,
        params: { id: result.objectId.toString() },
      };
    }
  );
  public static assembly = new SearchResultItemType(
    "Assembly",
    EnumBmEntityType.assembly.translation,
    (result) => {
      return {
        name: routes.productionMenu.assembly.view,
        params: { id: result.objectId.toString() },
      };
    }
  );
  public static priceList = new SearchResultItemType(
    "PriceList",
    EnumBmEntityType.priceList.translation,
    (result) => priceListSubRoutes.pricesTab.location(result.objectId)
  );

  public static supplier = new SearchResultItemType(
    "Supplier",
    EnumBmEntityType.supplier.translation,
    (result) => routes.contactsMenu.viewSupplier(result.objectId)
  );

  public static purchaseOrder = new SearchResultItemType(
    "PurchaseOrder",
    EnumBmEntityType.purchaseOrder.translation,
    (result) =>
      routes.stockMenu.purchaseOrdering.viewPurchaseOrder(result.objectId)
  );

  public static ullage = new SearchResultItemType(
    "Ullage",
    EnumBmEntityType.ullage.translation,
    (result) => ullageRoutes.view.location(result.objectId)
  );

  public static container = new SearchResultItemType(
    "Container",
    messages.search_type_container,
    (result) => routes.containerTrackingMenu.viewContainer(result.objectId)
  );

  public static lead = new SearchResultItemType(
    "Lead",
    translate(keys.leads_noun).toString(),
    (result) => routes.salesMenu.leads.viewLead(result.objectId)
  );

  public static productBrand = new SearchResultItemType(
    "ProductBrand",
    translate(keys.product_brand).toString(),
    (result) =>
      routes.stockMenu.productBrands.editLocation(result.objectId, false)
  );

  public static stockTake = new SearchResultItemType(
    "StockTake",
    translate(keys.stock_take).toString(),
    (result) => routes.stockMenu.stockTake.viewLocation(result.objectId)
  );

  public static stockTransfer = new SearchResultItemType(
    "StockTransfer",
    translate(keys.stock_transfer_noun).toString(),
    (result) =>
      routes.stockMenu.stockMovements.stockTransfer.editStockTransfer(
        result.objectId,
        false
      )
  );

  public static stockAdjustment = new SearchResultItemType(
    "StockAdjustment",
    translate(keys.stock_adjustment_noun).toString(),
    (result) =>
      routes.stockMenu.stockMovements.stockAdjustment.viewStockAdjustment(
        result.objectId
      )
  );

  public static pallet = new SearchResultItemType(
    "Pallet",
    translate(keys.pallet_noun).toString(),
    (result) => routes.distributionMenu.pallets.viewLocation(result.objectId)
  );

  public static tenantGroup = new SearchResultItemType(
    "TenantGroup",
    messages.enum_noun_tenant_group,
    (result) =>
      signInWithQuery({
        support: result.description,
      })
  );

  public static poDeliveryService = new SearchResultItemType(
    "PoDeliveryService",
    messages.po_delivery_service,
    (result) =>
      adminRoutes.editPoDeliveryService.location({
        id: result.objectId,
        editing: false,
      })
  );

  public key: string;
  public displayText: string;
  public routerLocationResolver: (result: TypedSearchResult) => RawLocation;

  private constructor(
    key: string,
    displayText: string,
    routerLocationResolver: (result: TypedSearchResult) => RawLocation
  ) {
    this.key = key;
    this.displayText = displayText;
    this.routerLocationResolver = routerLocationResolver;
    SearchResultItemType.allValues.push(this);
  }

  public static getValues(): SearchResultItemType[] {
    return SearchResultItemType.allValues;
  }

  public static getByKey(key: string): SearchResultItemType {
    const found = SearchResultItemType.allValues.find((e) => e.key === key);
    if (found === undefined) {
      throw new Error(`SearchResultItemType - Key not found: ${key}`);
    }

    return found;
  }
}

export class TypedSearchResult {
  result: SearchResultItem;

  constructor(result: SearchResultItem) {
    this.result = result;
  }

  // The router location object to navigate tothis search result item.
  get navigationRoute(): RawLocation {
    return this.type.routerLocationResolver(this);
  }

  get type(): SearchResultItemType {
    return SearchResultItemType.getByKey(this.result.type);
  }

  get description(): string {
    return this.result.description;
  }

  get objectId(): Guid {
    return this.result.objectId;
  }

  get isHidden(): boolean {
    return this.result.isHidden;
  }
}

export default class SearchServiceExtended extends SearchService {
  public async typedSearch(
    tenantId: Guid,
    searchQuery: string
  ): Promise<TypedSearchResult[]> {
    const query = new SearchQuery();
    query.query = searchQuery;

    return super.search(tenantId, query).then((value) =>
      value.searchResultItems
        .map((v) => new TypedSearchResult(v))
        .sort((a, b) => {
          if (a.result.isHidden === b.result.isHidden) {
            return 0;
          }
          if (a.result.isHidden) {
            return 1;
          }
          return -1;
        })
    );
  }
}

export const searchService = new SearchServiceExtended();
